/** Compiler output only. Runtime/test stderr must never enter this parser. */
export type CompileDiagnostic = { line: number; column: number; message: string };

function completeLines(output: string): string[] {
  const normalized = output.replace(/\r\n?/g, "\n").replace(/\u001b\[[0-9;]*m/g, "");
  const notice = normalized.lastIndexOf("\n[output truncated]");
  // A capped message may finish halfway through a diagnostic. Drop that line.
  const text = notice >= 0 ? normalized.slice(0, normalized.lastIndexOf("\n", notice - 1) + 1) : normalized;
  return text.split("\n");
}

/**
 * `EX-FNC05`: on a function problem the student's file is `solution.c`,
 * `solution.cpp`, `Solution.java`, `solution.py` or `solution.js` beside the
 * jury's grader (`Main.java`, `grader.cpp`, ...), so only the student's own
 * file is read; a line in the grader is never marked on the editor.
 */
export type DiagnosticsOptions = { functionProblem?: boolean };

export function parseCompileDiagnostics(
  languageKey: string | null,
  output: string,
  options: DiagnosticsOptions = {},
): CompileDiagnostic[] {
  const lines = completeLines(output);
  const stem = options.functionProblem ? "solution" : "main";
  const javaFile = options.functionProblem ? "Solution" : "Main";
  const errors: CompileDiagnostic[] = [];
  const add = (line: string | undefined, column: string | number | undefined, message: string | undefined) => {
    const at = Number(line);
    const col = Number(column ?? 1);
    if (Number.isSafeInteger(at) && at > 0 && Number.isSafeInteger(col) && col > 0 && message?.trim()) {
      errors.push({ line: at, column: col, message: message.trim() });
    }
  };
  for (let i = 0; i < lines.length && errors.length < 100; i++) {
    const line = lines[i] ?? "";
    let match: RegExpMatchArray | null;
    switch (languageKey) {
      case "c":
      case "cpp":
        match = line.match(new RegExp(`(?:^|/)${stem}\\.(?:c|cpp):(\\d+):(\\d+):\\s*(?:fatal )?error:\\s*(.+)`));
        if (match) add(match[1], match[2], match[3]);
        break;
      case "java":
        match = line.match(new RegExp(`(?:^|/)${javaFile}\\.java:(\\d+):\\s*error:\\s*(.+)`));
        if (match) {
          const caret = lines[i + 2]?.indexOf("^") ?? -1;
          add(match[1], caret >= 0 ? caret + 1 : 1, match[2]);
        }
        break;
      case "go":
        match = line.match(/(?:^|\/)main\.go:(\d+):(\d+):\s*(.+)/);
        if (match && !/^warning\b/i.test(match[3] ?? "")) add(match[1], match[2], match[3]);
        break;
      case "rust":
        if (/^error(?:\[E\d+\])?:/.test(line.trim())) {
          const location = lines.slice(i + 1, i + 5).map((part) => part.match(/-->\s*(?:.*\/)?main\.rs:(\d+):(\d+)/)).find((part) => part !== null);
          if (location) add(location[1], location[2], line.replace(/^\s*error(?:\[E\d+\])?:\s*/, ""));
        }
        break;
      case "typescript":
        match = line.match(/(?:^|\/)main\.ts\((\d+),(\d+)\):\s*error\s+TS\d+:\s*(.+)/);
        if (match) add(match[1], match[2], match[3]);
        break;
      case "python":
        match = line.match(new RegExp(`File "(?:.*/)?${stem}\\.py", line (\\d+)`));
        if (match) {
          const tail = lines.slice(i + 1, i + 6);
          const message = tail.find((part) => /^(?:SyntaxError|IndentationError|TabError):/.test(part.trim()));
          const caretLine = tail.find((part) => part.includes("^"));
          // py_compile indents both source and caret by four display spaces.
          const column = caretLine ? Math.max(1, caretLine.indexOf("^") - 3) : 1;
          add(match[1], column, message);
        }
        break;
      case "javascript":
        match = line.match(new RegExp(`(?:^|/)${stem}\\.js:(\\d+)\\s*$`));
        if (match) {
          const tail = lines.slice(i + 1, i + 6);
          const message = tail.find((part) => /^SyntaxError:/.test(part.trim()));
          const caretLine = tail.find((part) => part.includes("^"));
          add(match[1], caretLine ? caretLine.indexOf("^") + 1 : 1, message);
        }
        break;
      case "php":
        match = line.match(/(?:PHP )?Parse error:\s*(.+) in (?:.*\/)?main\.php on line (\d+)/);
        if (match) add(match[2], 1, match[1]);
        break;
      case "ruby":
        match = line.match(/(?:^|\/)main\.rb:(\d+):\s*(syntax error.+)/);
        if (match) {
          const caret = lines[i + 2]?.indexOf("^") ?? -1;
          add(match[1], caret >= 0 ? caret + 1 : 1, match[2]);
        }
        break;
    }
  }
  return errors;
}

/** This gate is shared by result messages and markers, including count_only. */
export function submissionCompileFeedback(view: {
  status: string; verdict: string | null; languageKey: string | null; compileOutput: string | null;
  /** `EX-FNC05`: the submission is to a function problem. */
  functionProblem?: boolean;
} | null): { output: string | null; errors: CompileDiagnostic[] } {
  if (!view || view.status !== "completed" || view.verdict !== "compilation_error" || !view.compileOutput) {
    return { output: null, errors: [] };
  }
  return {
    output: view.compileOutput,
    errors: parseCompileDiagnostics(view.languageKey, view.compileOutput, { functionProblem: view.functionProblem }),
  };
}

/** Public samples/custom runs still parse compileOutput only, never stderr. */
export function runCompileFeedback(track: {
  phase: string;
  result: { compiled: boolean; compileOutput: string | null } | null;
  input?: { languageKey: string; languageVersion: string; sourceCode: string };
} | null | undefined, draft?: { languageKey: string; languageVersion: string; sourceCode: string } | null,
options: DiagnosticsOptions = {}) {
  const output = track?.phase === "done" && track.result?.compiled === false ? track.result.compileOutput : null;
  const errors = output ? parseCompileDiagnostics(track?.input?.languageKey ?? null, output, options) : [];
  const matches = draft && track?.input && draft.languageKey === track.input.languageKey &&
    draft.languageVersion === track.input.languageVersion && draft.sourceCode === track.input.sourceCode;
  return { output, errors, markers: matches ? errors : [] };
}
